import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import {
	CreateCustomEvent,
	CustomEvent,
	CustomEventResponse,
	CustomEventResponseSchema,
	CustomEventSchema,
} from "../../pages/CustomEvent/schema/customEvent";
import {
	ScrapeRequest,
	ScrapeRequestSchema,
	ScrapeRequestsSchema,
} from "../../pages/CustomEvent/schema/scrapeRequest";

import { scrapeCreditTags } from "./scrapeCreditTags";

export const customEventApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getCustomEvents: builder.query<CustomEventResponse, RequestPaginationParams>({
			query: (params) => ({
				url: "customEvent",
				params: {
					...(params && { ...params }),
				},
				responseSchema: CustomEventResponseSchema,
			}),
			providesTags: ["CustomEvent"],
		}),
		getCustomEvent: builder.query<CustomEvent, number>({
			query: (id: number) => ({
				url: `customEvent/${id}`,
				params: {
					id,
				},
				responseSchema: CustomEventSchema,
			}),
			providesTags: ["CustomEvent"],
		}),
		// getCustomEventById: builder.query<SavedCustomEventResponse, number>({
		// 	query: (id) => ({
		// 		url: `customEvent/${id}`,
		// 		responseSchema: SavedCustomEventSchema,
		// 	}),
		// 	providesTags: [{ type: "CustomEvents", id: "DETAIL" }],
		// }),
		triggerCustomEvent: builder.mutation<void, number>({
			query: (id) => ({
				url: `customEvent/trigger/${id}`,
				method: "POST",
			}),
			invalidatesTags: ["CustomEvent"],
		}),
		createCustomEvent: builder.mutation<Partial<CustomEvent>, CreateCustomEvent>({
			query: (body) => ({
				url: `customEvent`,
				method: "POST",
				body,
				responseSchema: CustomEventSchema,
			}),
			// Creating a source charges its first scrape (PREC-4456).
			invalidatesTags: (result, error, arg) => scrapeCreditTags(arg.listId),
		}),
		assignCsvEnrichmentToCustomEvent: builder.mutation<
			Partial<CustomEvent>,
			{ customEventId: number; csvEnrichmentIds: number[] }
		>({
			query: ({ customEventId, csvEnrichmentIds }) => ({
				url: `customEvent/assign/${customEventId}`,
				method: "POST",
				body: { csvEnrichmentIds },
				responseSchema: CustomEventSchema,
			}),
			invalidatesTags: ["CustomEvent"],
		}),
		updateCustomEvent: builder.mutation<Partial<CustomEvent>, CreateCustomEvent>({
			query: ({ id, ...body }) => ({
				url: `customEvent/${id}`,
				method: "PATCH",
				body,
				responseSchema: CustomEventSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "CustomEvent"];
				}

				return ["CustomEvent"];
			},
		}),
		/** Orders another scrape of an already-delivered source — charges one scrape credit. */
		requestRescrape: builder.mutation<ScrapeRequest, { customEventId: number; listId?: number }>({
			query: ({ customEventId }) => ({
				url: `customEvent/${customEventId}/rescrape`,
				method: "POST",
				responseSchema: ScrapeRequestSchema,
			}),
			invalidatesTags: (result, error, arg) => scrapeCreditTags(arg.listId),
		}),
		getScrapeRequests: builder.query<ScrapeRequest[], number>({
			query: (customEventId) => ({
				url: `customEvent/${customEventId}/scrape-requests`,
				responseSchema: ScrapeRequestsSchema,
			}),
			providesTags: ["CustomEvent"],
		}),
		cancelScrapeRequest: builder.mutation<ScrapeRequest, { requestId: number; listId?: number }>({
			query: ({ requestId }) => ({
				url: `customEvent/scrape-request/${requestId}`,
				method: "DELETE",
				responseSchema: ScrapeRequestSchema,
			}),
			invalidatesTags: (result, error, arg) => scrapeCreditTags(arg.listId),
		}),
		/** Admin: uploads the scraped file, attaches it to the source and closes its open request. */
		deliverScrapeFile: builder.mutation<ScrapeRequest, { customEventId: number; form: FormData }>({
			query: ({ customEventId, form }) => ({
				url: `customEvent/${customEventId}/deliver`,
				method: "POST",
				body: form,
				responseSchema: ScrapeRequestSchema,
			}),
			invalidatesTags: ["CustomEvent", "CsvEnrichment"],
		}),
		/** Admin: closes an open request against a file attached out of band. */
		markScrapeRequestDelivered: builder.mutation<ScrapeRequest, { requestId: number }>({
			query: ({ requestId }) => ({
				url: `customEvent/scrape-request/${requestId}/deliver`,
				method: "POST",
				responseSchema: ScrapeRequestSchema,
			}),
			invalidatesTags: ["CustomEvent"],
		}),
		/** Forgets the login credentials shared for a source, so new ones can be provided. */
		resetCustomEventAccess: builder.mutation<
			Partial<CustomEvent>,
			{ customEventId: number; listId?: number }
		>({
			query: ({ customEventId }) => ({
				url: `customEvent/${customEventId}/reset-access`,
				method: "POST",
				responseSchema: CustomEventSchema,
			}),
			invalidatesTags: (result, error, arg) =>
				arg.listId ? [{ type: "List", id: arg.listId }, "CustomEvent"] : ["CustomEvent"],
		}),
		resetCustomEvent: builder.mutation<Partial<CustomEvent>, number>({
			query: (id) => ({
				url: `customEvent/${id}/reset`,
				method: "DELETE",
				responseSchema: CustomEventSchema,
			}),
			invalidatesTags: ["CustomEvent"],
		}),
	}),
});

export const {
	useGetCustomEventsQuery,
	useLazyGetCustomEventsQuery,
	useGetCustomEventQuery,
	useResetCustomEventMutation,
	useTriggerCustomEventMutation,
	useCreateCustomEventMutation,
	useUpdateCustomEventMutation,
	useAssignCsvEnrichmentToCustomEventMutation,
	useRequestRescrapeMutation,
	useGetScrapeRequestsQuery,
	useCancelScrapeRequestMutation,
	useResetCustomEventAccessMutation,
	useMarkScrapeRequestDeliveredMutation,
	useDeliverScrapeFileMutation,
} = customEventApi;
export const accountApiReducer = customEventApi.reducer;
export const accountApiMiddleware = customEventApi.middleware;
